// =====================================================================================
// INVRT Radar — web console — account — Data rights tab
//
// The organisation's data export and the single-person erasure request. Moved verbatim
// out of AccountPage.tsx in the C1 split; the page shell registers it under the tab id
// "rights".
// =====================================================================================

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download, ShieldAlert } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { downloadTextFile, timestampedFilename } from "@/lib/csv";
import { formatDateTime } from "@/lib/format";
import type { ComplianceErasureResponse, ComplianceExportResponse } from "@/lib/types";

export function DataRightsPanel() {
  const api = useApiClient();
  const { push } = useToast();
  const [erasureEmail, setErasureEmail] = useState("");
  const [confirming, setConfirming] = useState(false);

  const exportBundle = useMutation({
    mutationFn: () => api.post<ComplianceExportResponse>("/compliance/export", {}),
    onSuccess: (bundle) => {
      downloadTextFile(
        timestampedFilename("radar-data-export", "json"),
        JSON.stringify(bundle, null, 2),
        "application/json",
      );
    },
  });

  const erasure = useMutation({
    mutationFn: () =>
      api.post<ComplianceErasureResponse>("/compliance/erasure", {
        subject_email: erasureEmail.trim(),
      }),
    onSuccess: (result) => {
      setConfirming(false);
      setErasureEmail("");
      push({
        tone: "success",
        title: "Erasure queued",
        body: `The address is suppressed already. Job ${result.job_id} carries out the rest.`,
      });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Export your organisation&rsquo;s data</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            A JSON bundle of what Radar holds for your organisation: your organisation
            record, your suppression list, your context profiles, your credit ledger and
            your sites. Revealed contacts are reported as counts — you read those rows
            through the job that produced them. It never contains credentials or
            INVRT&rsquo;s own cost data.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div>
            <Button onClick={() => exportBundle.mutate()} loading={exportBundle.isPending}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Download my data
            </Button>
          </div>
          {exportBundle.isError ? <ErrorState error={exportBundle.error} /> : null}
          {exportBundle.isSuccess ? (
            <p className="text-sm text-accent">
              Bundle generated {formatDateTime(exportBundle.data.generated_at)}. Sections
              holding more rows than fit are marked as truncated inside the file.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Erase one person&rsquo;s data</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            For a single email address within your organisation. Radar is honest about what
            this does and does not do — read it before you use it.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="rounded-lg border border-line bg-surface-subtle px-4 py-3 text-sm text-ink-muted">
            <p className="font-medium text-ink">What happens immediately</p>
            <ul className="mt-1 list-inside list-disc">
              <li>The address is added to your suppression list, so it is never re-acquired.</li>
              <li>An erasure job is queued and you can follow it by its job id.</li>
            </ul>
            <p className="mt-3 font-medium text-ink">What the job then does</p>
            <ul className="mt-1 list-inside list-disc">
              <li>Deletes revealed contact records and context profiles matching the address.</li>
              <li>
                Clears the person details on matching visitor events — the event row itself
                stays, because it is a billing record.
              </li>
            </ul>
            <p className="mt-3 font-medium text-ink">What is kept, deliberately</p>
            <ul className="mt-1 list-inside list-disc">
              <li>
                Your credit ledger and audit log. Neither carries a person&rsquo;s details, and
                both are append-only financial records.
              </li>
            </ul>
            <p className="mt-3">
              This is a mechanism, not legal advice. Confirm it meets your own obligations
              before relying on it as your sole erasure process.
            </p>
          </div>

          <Input
            label="Email address to erase"
            type="email"
            value={erasureEmail}
            onChange={(event) => setErasureEmail(event.target.value)}
            placeholder="person@example.com"
            hint="Radar records only the hash of this address in its audit trail, never the address itself."
          />

          <div>
            <Button
              variant="danger"
              disabled={erasureEmail.trim() === ""}
              onClick={() => setConfirming(true)}
            >
              <ShieldAlert className="h-4 w-4" aria-hidden="true" />
              Request erasure
            </Button>
          </div>

          {erasure.isError ? <ErrorState error={erasure.error} /> : null}
        </CardContent>
      </Card>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Erase this person's data?"
        description={`This suppresses ${erasureEmail} immediately and queues the deletion. It cannot be undone.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep the data
            </Button>
            <Button variant="danger" onClick={() => erasure.mutate()} loading={erasure.isPending}>
              Erase this person
            </Button>
          </>
        }
      >
        <p className="text-ink-muted">
          Revealed contact records and context profiles for this address are deleted. The
          person details on their visitor events are cleared whilst the billing record
          remains. Your ledger and audit log are untouched.
        </p>
      </Dialog>
    </div>
  );
}
